import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Text } from 'recharts';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { uniqueId } from 'lodash';
import { format } from '@origo_vl_banking/utils';
import Loader from '../loader/Loader';

const TooltipContainer = styled.div`
  background-color: white;
  opacity: 0.95;
  border: thin solid ${props => props.theme.borderColor};
`;

const PieChartContainer = styled.div`
  overflow: hidden;
  width: 100%;
  @media (max-width: 870px) {
    overflow-x: auto;
  }
`;

const LoaderContainer = styled.div`
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledResponsiveContainer = styled(ResponsiveContainer)`
  margin: 0 auto;
`;

const label = (e, labelTextWidth) => {
  const textAnchor = e.x > e.cx ? 'start' : 'end';

  return (
    <Text
      x={e.x}
      y={e.y}
      alignmentBaseline="middle"
      className="recharts-text recharts-pie-label-text"
      fill="black"
      width={labelTextWidth}
      textAnchor={textAnchor}
      style={{ fontSize: 12, fontWeight: 500 }}
    >
      {e.name}
    </Text>
  );
};

const renderTooltip = (e, units, tooltipValueKey) => {
  if (!e.active) {
    return null;
  }
  const { payload } = e.payload[0];
  let value = payload[tooltipValueKey];

  if (units === '%') {
    value *= 100;
  }

  return (
    <TooltipContainer>
      <div style={{ padding: 10, display: 'flex' }}>
        <div style={{ color: payload.fill, fontWeight: 'bolder' }}>{payload.Name}</div>
        <div style={{ paddingLeft: 10 }}>
          {format.value(value)} {units}
        </div>
      </div>
    </TooltipContainer>
  );
};

const OrigoPieChart = ({
  data,
  dataKey,
  nameKey,
  theme,
  units,
  loading,
  height,
  width,
  aspect,
  onClick,
  tooltipValueKey,
  tooltip,
  labelTextWidth,
}) => (
  <PieChartContainer>
    <StyledResponsiveContainer
      style={{ margin: '0 auto' }}
      height={height}
      width={width}
      aspect={aspect}
      debounce={50}
    >
      {loading ? (
        <LoaderContainer>
          <Loader size={70} color={theme.loaderColor} />
        </LoaderContainer>
      ) : (
        <PieChart style={{ margin: 'auto' }}>
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            labelLine
            label={e => label(e, labelTextWidth)}
            cy="50%"
            cx="50%"
            innerRadius="50%"
            outerRadius="70%"
            paddingAngle={5}
            isAnimationActive={false}
            minAngle={3}
          >
            {data.map((item, index) => (
              <Cell
                key={uniqueId()}
                onClick={() => onClick(item)}
                fill={theme && theme.pieChartColors[index % theme.pieChartColors.length]}
              />
            ))}
          </Pie>
          <Tooltip
            content={tooltip || (e => renderTooltip(e, units, tooltipValueKey))}
            theme={theme}
          />
        </PieChart>
      )}
    </StyledResponsiveContainer>
  </PieChartContainer>
);
OrigoPieChart.propTypes = {
  theme: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  units: PropTypes.string,
  dataKey: PropTypes.string.isRequired,
  nameKey: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  aspect: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func,
  tooltipValueKey: PropTypes.string,
  tooltip: PropTypes.func,
  labelTextWidth: PropTypes.number,
};

OrigoPieChart.defaultProps = {
  units: 'ISK',
  // Some components do not rely on the supplied loader,
  // instead they render their custom loader.
  // It is therefore the callers' responsibility to declare if it wants to use the loader.
  loading: false,
  height: 256,
  width: '100%',
  aspect: null,
  onClick: () => null,
  tooltipValueKey: 'Value',
  tooltip: null,
  labelTextWidth: 100,
};

export default withTheme(OrigoPieChart);
